<template>
  <v-card>
    <div class="d-flex pa-4">
      <span class="headline primary--text font-weight-medium">
        <v-icon class="primary--text" left>mdi-handshake</v-icon>Bulk
        Transactions
      </span>
      <v-spacer></v-spacer>
      <excel-download-button
        v-if="isClientConnectedToServer"
        class="mx-2"
        :size="headerButtonSize"
        :onClick="exportAsExcel"
        :isLoading="isExportingExcel"
      />
      <csv-download-button
        :size="headerButtonSize"
        :data="bulkTransactionsForCsvExport"
        :fields="csvExportFields"
        :labels="csvExportLabels"
        :fileName="getExportFileName()"
      />
    </div>
    <bulk-transactions-table
      :items="bulkTransactions"
      :groupBy="'vesselActivity.id'"
      :DeleteTransaction="deleteBulkTransaction"
      v-on:editTransaction="editBulkTransaction"
      :items-per-page="15"
      :disableConsumptionEdit="true"
    />
    <v-dialog v-model="showFormDialog" max-width="850px">
      <v-card>
        <bulk-transaction-form
          v-if="selectedTransaction"
          :transaction="selectedTransaction"
          :vesselId="voyageReport.vesselId"
          :onSubmitClick="submitBulkTransactionEdit"
          :onUpdateAttachmentClick="updateBulkDeliveryNoteFile"
          :removeBulkDeliveryNote="removeBulkDeliveryNoteFile"
          :activity="selectedTransaction.vesselActivity"
        />
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import BulkTransactionsTable from './BulkTransactionsTable.vue';
import BulkTransactionForm from './BulkTransactionForm.vue';
import BulkTransactionsHelperMixin from '../../mixins/BulkTransactionsHelpersMixin';
import ResponsiveSizeHelperMixin from '../../mixins/ResponsiveSizeHelperMixin';
import ExcelDownloadButton from '../ui/ExcelDownloadButton.vue';
import DownloadHelperMixin from '../../mixins/DownloadHelperMixin';
import CsvDownloadButton from '../ui/CsvDownloadButton.vue';
import dateTimeHelpers from '../../mixins/DateTimeHelpers';

export default {
  name: 'BulkTransactionsOverview',
  components: {
    BulkTransactionsTable,
    BulkTransactionForm,
    ExcelDownloadButton,
    CsvDownloadButton,
  },
  mixins: [BulkTransactionsHelperMixin, ResponsiveSizeHelperMixin, DownloadHelperMixin],
  data() {
    return {
      showFormDialog: false,
      isExportingExcel: false,
      csvExportFields: [
        'transactionType',
        'bulk',
        'quantity',
        'qtyUom',
        'location',
        'completedDateTimeLt',
        'bulkDeliveryNoteNumber',
        'comments',
        'vesselName',
      ],
      csvExportLabels: {
        transactionType: 'Transaction Type',
        bulk: 'Bulk',
        quantity: 'Quantity',
        qtyUom: 'Unit of Measurement',
        location: 'Location',
        completedDateTimeLt: 'Completed At (LT)',
        bulkDeliveryNoteNumber: 'Bulk Delivery Note Number',
        comments: 'Comments',
        vesselName: 'Vessel Name',
      },
    };
  },
  computed: {
    ...mapState(['voyageReportsStore', 'userStore', 'locationsStore']),
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getSyncedAndUnSyncedVesselActivities: 'voyageReportsStore/getSyncedAndUnSyncedVesselActivities',
      getAllUnsyncedBulkTransactions: 'dataSyncStore/getAllUnsyncedBulkTransactions',
    }),
    bulkTransactions() {
      const transactions = this.getSyncedAndUnSyncedVesselActivities
        .filter((x) => x.bulkTransactions.length > 0)
        .flatMap((va) => va.bulkTransactions.map((bt) => (
          {
            ...bt,
            vesselActivity: va,
          }
        )));

      const unsyncedBulkTransactions = this.getAllUnsyncedBulkTransactions
        .filter((x) => x.voyageReportId === this.voyageReport.id);

      return transactions.concat(unsyncedBulkTransactions);
    },
    bulkTransactionsForCsvExport() {
      return this.bulkTransactions;
    },
    voyageReport() {
      return this.voyageReportsStore ? this.voyageReportsStore.voyageReportDetailsData : null;
    },
  },
  methods: {
    editBulkTransaction(transaction) {
      this.selectedTransaction = { ...transaction };
      this.setSelectedBulkTransactionInputValues();
      this.showFormDialog = true;
    },
    submitBulkTransactionEdit() {
      const transactionUpdate = this.getBulkTransactionUpdateModel(this.selectedTransaction);
      this.$store.dispatch('bulkTransactionsStore/updateBulkTransaction', transactionUpdate)
        .then((resp) => {
          this.onSuccess('Bulk Transaction Updated', resp);
          this.showFormDialog = false;
          this.$nextTick(() => {
            this.selectedTransaction = null;
          });
        })
        .catch((err) => this.onError('Failed to Edit Bulk Transaction', err));
    },
    deleteBulkTransaction(id) {
      this.$store.dispatch('bulkTransactionsStore/deleteBulkTransaction', id)
        .then((resp) => {
          this.onSuccess('Bulk Transaction Deleted', resp);
        })
        .catch((err) => this.onError('Failed To Delete Bulk Transaction', err));
    },
    updateBulkDeliveryNoteFile(transaction) {
      return this.addBulkDeliveryNoteToTransaction(transaction.bulkDeliveryNoteFile, transaction.id)
        .then(() => {
          this.onSuccess('Bulk Delivery Note added to Transaction');
        })
        .catch((err) => this.onError(`Failed to attach note to transaction: ${err.message}`));
    },
    removeBulkDeliveryNoteFile(transactionId) {
      return this.$store.dispatch('bulkTransactionsStore/removeBulkDeliveryNote', transactionId)
        .then(() => {
          this.onSuccess('Bulk Delivery Note Removed');
          const refreshedTransaction = this.bulkTransactions.find((x) => x.id === transactionId);
          this.selectedTransaction = { ...refreshedTransaction };
          this.setSelectedBulkTransactionInputValues();
        })
        .catch((err) => this.onError(`Failed to remove note from transaction: ${err.message}`));
    },
    getExportFileName() {
      const siteId = this.voyageReport ? this.voyageReport.siteId : '';
      const voyageNo = this.voyageReport ? this.voyageReport.voyageNo : '';
      const dateTime = dateTimeHelpers.getFileTimestampNow();
      return `${siteId}_VoyageNo-${voyageNo}_BulkTransactions-${dateTime}.csv`;
    },
    exportAsExcel() {
      this.isExportingExcel = true;
      this.downloadFile('voyageReportsStore/getVoyageReportAsExcelFile', this.voyageReport.id)
        .finally(() => { this.isExportingExcel = false; });
    },
    onSuccess(message, data) {
      this.$toast.success(message);
    },
    onError(message, data) {
      this.$toast.error(message);
      console.error(message, data);
    },
  },
};
</script>

<style>
</style>
